<script setup lang="ts">
// vue
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

// stores
import { Distribution, DistributionExpanded, useEntityStore } from '@/modules/investing/stores/better-entity-store'
import { useDistributionStore } from '@/modules/investing/stores/distribution-store'

// composables
import { useModal } from '@/modules/shared/composables/use-modal'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'

// utils
import { format } from '@/modules/shared/utils/v-table'
import { initialMoney, toNumber } from '@/modules/shared/utils/money'

// shared components
import { VButton, VModal } from '@/modules/shared/components'
import DescriptionList from '@/modules/shared/components/DescriptionList.vue'
import StackedChartSummaryDistribution from '@/modules/shared/components/StackedChartSummaryDistribution.vue'
import VBadge from '@/modules/shared/components/VBadge.vue'
import VLoading from '@/modules/shared/components/v-loading.vue'
import { capitalize } from 'lodash'
import { investorPath } from '@/modules/investing/utils/investor'
import { rails_url } from '@/modules/shared/utils/rails'
import DistributionPaymentForm from './DistributionPaymentForm.vue'

interface Props {
  distributionId: string
  isAdmin?: boolean
  refetch?: () => Promise<void>
}

const props = defineProps<Props>()

const { slug } = useRoute().params as { slug: string }

const entityStore = useEntityStore()
const store = useDistributionStore()
const distribution = ref<Distribution>({} as Distribution)
const expandedDistribution = computed((): DistributionExpanded => {
  if (!distribution.value) return null
  return {
    ...distribution.value,
    company: entityStore.getCompany(distribution.value.company_id),
  } as DistributionExpanded
})

const modal = useModal()

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

hideSkeleton()

const fetchAndOpenModal = async (distributionId: string) => {
  showSkeleton()
  modal.open()
  distribution.value = await store.fetchDistribution(distributionId, { slug })
  hideSkeleton()
}

// -----------------------------------------------------------------------------
// Charts
// -----------------------------------------------------------------------------

const grossDistributed = computed(() => toNumber(expandedDistribution.value?.gross ?? initialMoney))
const returnOfCapital = computed(() => toNumber(expandedDistribution.value?.return_of_capital ?? initialMoney))
const preferredReturn = computed(() => toNumber(expandedDistribution.value?.preferred_return ?? initialMoney))
const profit = computed(() => toNumber(expandedDistribution.value?.profit ?? initialMoney))
const carriedInterest = computed(() => toNumber(expandedDistribution.value?.carried_interest ?? initialMoney))
const distributedFees = computed(() => toNumber(expandedDistribution.value?.other_fee ?? initialMoney))

const bankDetails = computed(() => {
  const details = [
    { label: 'Payment Method', value: expandedDistribution.value?.investor?.disbursement_method || '--' },
    { label: 'Bank Name', value: expandedDistribution.value?.investor?.disbursement_wire_bank_name || '--' },
    { label: 'Bank Address', value: expandedDistribution.value?.investor?.disbursement_wire_bank_address || '--' },
    {
      label: 'Account Number',
      value: expandedDistribution.value?.investor?.disbursement_wire_bank_account_number || '--',
    },
    {
      label: 'Routing Number',
      value: expandedDistribution.value?.investor?.disbursement_wire_bank_routing_number || '--',
    },
    {
      label: 'For Further Credit To',
      value: expandedDistribution.value?.investor?.disbursement_wire_for_further_credit_to || '--',
    },
  ]

  return details.filter((detail) => detail.value !== '--')
})
</script>

<template>
  <template v-if="$slots.default">
    <slot :fetchAndOpenModal="() => fetchAndOpenModal(props.distributionId)"></slot>
  </template>
  <button v-else @click="fetchAndOpenModal(props.distributionId)" class="relative cursor-pointer">
    <div>View</div>
  </button>
  <VModal :modalStore="modal" :has_footer="false">
    <template #main>
      <div class="flex flex-col space-y-6">
        <div>
          <StackedChartSummaryDistribution
            :loading="skeleton"
            :grossDistributed="grossDistributed"
            :returnOfCapital="returnOfCapital"
            :preferredReturn="preferredReturn"
            :profit="profit"
            :carriedInterest="carriedInterest"
            :distributedFees="distributedFees"
            :currency="'USD'"
          />
        </div>
        <div>
          <DescriptionList
            :items="[
              { label: 'Investor', value: expandedDistribution.investor_name || '--', key: 'investor' },
              { label: 'Date', value: format(expandedDistribution.date, 'date') },
              { label: 'Type', value: capitalize(expandedDistribution.type) },
              ...(expandedDistribution.company
                ? [{ label: 'Company', value: expandedDistribution.company.name || '--', key: 'company' }]
                : []),
              { label: 'Recallable', value: format(expandedDistribution.is_recallable, 'boolean') },
              { label: 'Status', value: expandedDistribution.payment_status || '--', key: 'payment_status' },
              ...(expandedDistribution.payment_status === 'paid' && expandedDistribution.payment_status_marked_at
                ? [{ label: 'Paid Date', value: format(expandedDistribution.payment_status_marked_at, 'date') }]
                : []),
            ]"
            :loading="skeleton"
            title="Distribution Details"
          >
            <template #investor>
              <RouterLink
                v-if="expandedDistribution.investor?.cid"
                class="hyperlink"
                :to="investorPath(expandedDistribution.investor.cid)"
              >
                {{ expandedDistribution.investor_name }}
              </RouterLink>
              <span v-else>{{ expandedDistribution.investor_name || '--' }}</span>
            </template>
            <template #company>
              <a
                v-if="expandedDistribution.company?.id"
                class="hyperlink"
                :href="`${rails_url()}/companies/${expandedDistribution.company.id}`"
              >
                {{ expandedDistribution.company.name }}
              </a>
              <span v-else>{{ expandedDistribution.company?.name || '--' }}</span>
            </template>
            <template #payment_status>
              <VBadge
                color="V_DISTRIBUTION_RETURN_OF_CAPITAL"
                :dot="false"
                v-if="expandedDistribution.payment_status === 'paid'"
              >
                Paid
              </VBadge>
              <VBadge color="V_UNPAID" :dot="false" v-else>Unpaid</VBadge>
            </template>
          </DescriptionList>
        </div>
        <div v-if="bankDetails.length > 0">
          <DescriptionList :items="bankDetails" :loading="skeleton" title="Bank Details" />
        </div>
        <DistributionPaymentForm v-if="props.isAdmin" v-model="distribution" :refetch="refetch" />
      </div>
    </template>
  </VModal>
</template>
